import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import config from '../../../../../config';
import ChartBuilder from '../../../../../shared/components/chart/ChartBuilder';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { getChartTheme } from '../../../../../shared/components/chart/chartThemeHelper';
import { currencyFormatterDirect } from '../../../../../shared/components/table/functions';

export default function MetaCampaignReportLayout() {
  const gridRef = useRef();
  const gridApiRef = useRef(null);
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [graphData, setGraphData] = useState([{}]);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days'));
  const [endDate, setEndDate] = useState(moment().subtract(1, 'days'));
  const [tableData, setTableData] = useState([]);
  const [conversionScope, setConversionScope] = useState('purchase');

  const conversionScopeOptions = [
    { value: 'purchase', label: 'Purchase (Generic)', countKey: 'purchases', salesKey: 'purchase_sales', countName: 'Purchases', salesName: 'Purchase Sales' },
    { value: 'website', label: 'Website', countKey: 'website_purchases', salesKey: 'website_sales', countName: 'Website Purchases', salesName: 'Website Sales' },
    { value: 'omni', label: 'Omni', countKey: 'omni_purchases', salesKey: 'omni_sales', countName: 'Omni Purchases', salesName: 'Omni Sales' },
  ];

  const selectedScope = conversionScopeOptions.find(o => o.value === conversionScope);

  useEffect(() => {
    if (!sellerSelect.value) return;

    setApiLoad(true);
    setGraphData([{}]);
    setTableData([]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const reportUrl = `${config.serverUrl}/meta/campaign-report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;

    fetch(reportUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      })
      .then((data) => {
        if (data.status === 'success') {
          // Process graph data
          const formattedGraphData = (data.graphData || []).map(item => ({
            date: item.date,
            impressions: Number(item.impressions) || 0,
            reach: Number(item.reach) || 0,
            clicks: Number(item.clicks) || 0,
            inline_link_clicks: Number(item.inline_link_clicks) || 0,
            spend: Number(item.spend) || 0,
            purchases: Number(item.purchases) || 0,
            purchase_sales: Number(item.purchase_sales) || 0,
            website_purchases: Number(item.website_purchases) || 0,
            website_sales: Number(item.website_sales) || 0,
            omni_purchases: Number(item.omni_purchases) || 0,
            omni_sales: Number(item.omni_sales) || 0,
            purchase_roas: Number(item.purchase_roas) || 0,
            website_purchase_roas: Number(item.website_purchase_roas) || 0,
          }));
          setGraphData(formattedGraphData);

          // Process table data
          const formattedTableData = (data.tableData || []).map(item => ({
            ...item,
            impressions: Number(item.impressions) || 0,
            reach: Number(item.reach) || 0,
            clicks: Number(item.clicks) || 0,
            unique_clicks: Number(item.unique_clicks) || 0,
            inline_link_clicks: Number(item.inline_link_clicks) || 0,
            unique_inline_link_clicks: Number(item.unique_inline_link_clicks) || 0,
            spend: Number(item.spend) || 0,
            ctr: Number(item.ctr) || 0,
            cpc: Number(item.cpc) || 0,
            cpm: Number(item.cpm) || 0,
            cpp: Number(item.cpp) || 0,
            purchases: Number(item.purchases) || 0,
            purchase_sales: Number(item.purchase_sales) || 0,
            website_purchases: Number(item.website_purchases) || 0,
            website_sales: Number(item.website_sales) || 0,
            omni_purchases: Number(item.omni_purchases) || 0,
            omni_sales: Number(item.omni_sales) || 0,
            purchase_roas: Number(item.purchase_roas) || 0,
            website_purchase_roas: Number(item.website_purchase_roas) || 0,
          }));

          // Calculate totals
          const totals = {
            campaign_name: 'TOTAL',
            campaign_id: '',
            objective: '',
            buying_type: '',
            impressions: formattedTableData.reduce((sum, row) => sum + row.impressions, 0),
            reach: formattedTableData.reduce((sum, row) => sum + row.reach, 0),
            clicks: formattedTableData.reduce((sum, row) => sum + row.clicks, 0),
            unique_clicks: formattedTableData.reduce((sum, row) => sum + row.unique_clicks, 0),
            inline_link_clicks: formattedTableData.reduce((sum, row) => sum + row.inline_link_clicks, 0),
            unique_inline_link_clicks: formattedTableData.reduce((sum, row) => sum + row.unique_inline_link_clicks, 0),
            spend: formattedTableData.reduce((sum, row) => sum + row.spend, 0),
            purchases: formattedTableData.reduce((sum, row) => sum + row.purchases, 0),
            purchase_sales: formattedTableData.reduce((sum, row) => sum + row.purchase_sales, 0),
            website_purchases: formattedTableData.reduce((sum, row) => sum + row.website_purchases, 0),
            website_sales: formattedTableData.reduce((sum, row) => sum + row.website_sales, 0),
            omni_purchases: formattedTableData.reduce((sum, row) => sum + row.omni_purchases, 0),
            omni_sales: formattedTableData.reduce((sum, row) => sum + row.omni_sales, 0),
          };

          // Calculate derived metrics from totals
          totals.ctr = totals.impressions > 0 ? totals.clicks / totals.impressions : 0;
          totals.cpc = totals.clicks > 0 ? totals.spend / totals.clicks : 0;
          totals.cpm = totals.impressions > 0 ? (totals.spend / totals.impressions) * 1000 : 0;
          totals.cpp = totals.reach > 0 ? (totals.spend / totals.reach) * 1000 : 0;
          totals.purchase_roas = totals.purchase_sales > 0 ? totals.purchase_sales / totals.spend : 0;
          totals.website_purchase_roas = totals.website_sales > 0 ? totals.website_sales / totals.spend : 0;

          setTableData([...formattedTableData, totals]);
        }
        setApiLoad(false);
      })
      .catch((error) => {
        console.error('Error fetching Meta campaign report:', error);
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  const chartOptions = {
    theme: getChartTheme(theme),
    data: graphData,
    title: {
      text: 'Meta Campaign Performance',
    },
    series: [
      {
        type: 'line',
        xKey: 'date',
        yKey: 'impressions',
        yName: 'Impressions',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'reach',
        yName: 'Reach',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'inline_link_clicks',
        yName: 'Link Clicks',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: selectedScope.countKey,
        yName: selectedScope.countName,
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'spend',
        yName: 'Spend',
        yKeyAxis: 'ySecondary',
        tooltip: {
          renderer: (params) => {
            const formatted = currencyFormatterDirect(params.yValue);
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: selectedScope.salesKey,
        yName: selectedScope.salesName,
        yKeyAxis: 'ySecondary',
        tooltip: {
          renderer: (params) => {
            const formatted = currencyFormatterDirect(params.yValue);
            return { title: params.xValue, content: `${params.yName}: ${formatted}` };
          },
        },
      },
    ],
    axes: {
      y: {
        type: 'number',
        position: 'left',
        keys: ['impressions', 'reach', 'inline_link_clicks', selectedScope.countKey],
        title: {
          text: 'Count',
        },
      },
      ySecondary: {
        type: 'number',
        position: 'right',
        keys: ['spend', selectedScope.salesKey],
        title: {
          text: 'Amount',
        },
        label: {
          formatter: (params) => currencyFormatterDirect(params.value),
        },
        nice: true,
      },
    },
  };

  // Table column definitions
  const colDef = [
    {
      field: 'campaign_name',
      headerName: 'Campaign Name',
      sortable: true,
      filter: true,
      resizable: true,
      cellStyle: params => {
        if (params.data && params.data.campaign_name === 'TOTAL') {
          return { fontWeight: 'bold' };
        }
        return null;
      },
    },
    {
      field: 'campaign_id',
      headerName: 'Campaign ID',
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      field: 'objective',
      headerName: 'Objective',
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'reach',
      headerName: 'Reach',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'clicks',
      headerName: 'Clicks',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'inline_link_clicks',
      headerName: 'Link Clicks',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'spend',
      headerName: 'Spend',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'ctr',
      headerName: 'CTR',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `${(params.value * 100).toFixed(2)}%` : '0.00%'),
    },
    {
      field: 'cpc',
      headerName: 'CPC',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'cpm',
      headerName: 'CPM',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'cpp',
      headerName: 'CPP',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'purchases',
      headerName: 'Purchases',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'purchase_sales',
      headerName: 'Purchase Sales',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'purchase_roas',
      headerName: 'Purchase ROAS',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toFixed(2) : '0.00'),
    },
    {
      field: 'website_purchases',
      headerName: 'Website Purchases',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'website_sales',
      headerName: 'Website Sales',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'website_purchase_roas',
      headerName: 'Website ROAS',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toFixed(2) : '0.00'),
    },
    {
      field: 'omni_purchases',
      headerName: 'Omni Purchases',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'omni_sales',
      headerName: 'Omni Sales',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const useTheme = isDarkMode
    ? themeQuartz.withPart(colorSchemeDark)
    : themeQuartz.withPart(colorSchemeLight);

  const onBtnExport = () => {
    if (gridApiRef.current) {
      const fileName = `${sellerSelect.value}-Meta-CampaignReport-${moment().format('YYYYMMDD')}.csv`;
      gridApiRef.current.exportDataAsCsv({ fileName });
    }
  };

  return (
    <Container className="dashboard">
      <Col md={12}>
        <CardPanel
          showButtons={false}
          parentRefresh={apiLoad}
        >
          <Row>
            <Col lg={3}>
              <p>Dates:</p>
              <DateRangePicker
                startDate={startDate.toDate()}
                endDate={endDate.toDate()}
                onDateChange={dateChange}
              />
            </Col>
            <Col lg={3}>
              <p>Graph Conversion Scope:</p>
              <select
                className="form-control"
                value={conversionScope}
                onChange={e => setConversionScope(e.target.value)}
              >
                {conversionScopeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Col>
          </Row>
        </CardPanel>
        <Row>
          <CardPanel
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <ChartBuilder chartOptions={chartOptions} />
          </CardPanel>
        </Row>
      </Col>
      <Col md={12}>
        <CardPanel
          title=""
          subhead=""
          showButtons={false}
          parentRefresh={apiLoad}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button className="btn btn-outline-primary" style={{ width: '200px' }} onClick={onBtnExport} type="button">Export</button>
          </div>
          <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
            <AgGridReact
              theme={useTheme}
              ref={gridRef}
              columnDefs={colDef}
              rowData={tableData}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
            />
          </div>
        </CardPanel>
      </Col>
    </Container>
  );
}
